import { Pipe, PipeTransform } from '@angular/core';

/**
 * A custom Angular pipe for replacing whole word occurrences within a string.
 *
 * The `ReplaceWholeWordPipe` searches for occurrences of a word within a string,
 * and replaces them with the specified replacement. The search can be case-insensitive,
 * and also respects word boundaries to ensure that only whole words are replaced.
 * Optionally, spaces in the replacement word can be converted into non-breaking
 * spaces to preserve layout integrity.
 *
 * Usage:
 *
 * ```html
 * {{ 'Replace the word word' | replaceWholeWord:'word':'term' }}
 * {{ 'Replace the word word' | replaceWholeWord:'word':'non-breakable term':true }}
 * ```
 *
 * @example
 * ```typescript
 * const input = "Hello, world!";
 * const transformed = pipe.transform(input, "world", "everyone");
 * // Result: "Hello, everyone!"
 * ```
 *
 * @example
 * With non-breaking spaces enabled:
 * ```typescript
 * const input = "Keep spaces consistent";
 * const transformed = pipe.transform(input, "spaces", "non-breaking spaces", true);
 * // Result: "Keep non-breaking spaces consistent"
 * ```
 *
 * @export
 * @class ReplaceWholeWordPipe
 * @implements {PipeTransform}
 */
@Pipe({
  name: 'replaceWholeWord',
  standalone: true,
  pure: true,
})
export class ReplaceWholeWordPipe implements PipeTransform {
  transform(value: string, searchWord: string, replaceWord: string, nonBreakable: boolean = false): string {
    if (!value || !searchWord) {
      return value;
    }
    const escapedWord = searchWord.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
    const isPureWord = /^\w+$/.test(searchWord);
    let safeReplaceWord = replaceWord;
    if (nonBreakable) {
      safeReplaceWord = replaceWord.replace(/ /g, '\u00A0');
    }
    const pattern = isPureWord ? `\\b${escapedWord}\\b` : `${escapedWord}`;
    const regex = new RegExp(pattern, 'gi');
    return value.replace(regex, safeReplaceWord);
  }
}
